import { Divider, Flex } from 'antd';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { SelectOption } from '@/shared/types';
import {
  Button,
  ButtonVariant,
  ConditionView,
  DatePicker,
  DatePickerProps,
  Icon,
  Modal,
  Select,
  useModal,
} from '@/shared/ui';

import { isFormattedDate } from '@/components/Forms/Validation';

import { Breakpoints, useMediaQuery } from '@/hooks/useMediaQuery';

import { ColorConfig } from '@/contexts/AntdConfigProvider/theme';

import { UserRequestFiltersOptionsV2 } from '@/api/types';

import { SEDUserRequestChannels } from '../lib';
import { DEFAULT_PAGE, FILTER_QUERY_KEYS, PAGE_QUERY_KEY, UserRequestsFiltersState, UserRequestsTab } from '../model';

interface Props {
  tab: UserRequestsTab;
  filterOptions: Nullable<UserRequestFiltersOptionsV2>;
  isLoadingOptions?: boolean;
}

export const UserRequestsFilters: React.FC<Props> = ({ tab, filterOptions, isLoadingOptions = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOpen, openModal, closeModal } = useModal();
  const matchLG = useMediaQuery(Breakpoints.LG);

  const [filterValues, setFilterValues] = useState<UserRequestsFiltersState>({});

  const channelOptions = useMemo<Array<SelectOption<NonNullable<UserRequestsFiltersState['channel']>>>>(() => {
    return (filterOptions?.channels ?? []).map((channel) => ({
      id: channel,
      label: SEDUserRequestChannels[channel] || String(channel),
    }));
  }, [filterOptions]);

  const serviceOptions = useMemo<Array<SelectOption<NonNullable<UserRequestsFiltersState['service_code']>>>>(() => {
    return (filterOptions?.services ?? []).map(({ code, name }) => ({
      id: code,
      label: name,
    }));
  }, [filterOptions]);

  const statusOptions = useMemo<
    Array<SelectOption<NonNullable<UserRequestsFiltersState['service_status_name']>>>
  >(() => {
    return (filterOptions?.statuses ?? []).map(({ name }) => ({
      id: name,
      label: name,
    }));
  }, [filterOptions]);

  const createdAtStartConfig = useMemo<Pick<DatePickerProps, 'minDate' | 'maxDate'>>(() => {
    const fallbackMin = dayjs().subtract(100, 'years');
    const fallbackMax = dayjs();
    const from = filterOptions?.date_range?.created_at_from;
    const to = filterOptions?.date_range?.created_at_to;
    return {
      minDate: from ? dayjs(from) : fallbackMin,
      maxDate: to ? dayjs(to) : fallbackMax,
    };
  }, [filterOptions]);

  const createdAtEndConfig = useMemo<Pick<DatePickerProps, 'minDate' | 'maxDate'>>(() => {
    return {
      minDate: filterValues.created_at_from ? dayjs(filterValues.created_at_from) : createdAtStartConfig.minDate,
      maxDate: dayjs(),
    };
  }, [filterValues.created_at_from, createdAtStartConfig]);

  useEffect(() => {
    setFilterValues({});
  }, [tab]);

  useEffect(() => {
    if (!filterOptions) return;
    const defaultFilterValues = Array.from(searchParams.entries()).reduce<UserRequestsFiltersState>(
      (acc, [filterName, filterValue]) => {
        const correctlyTypedFilterName = filterName as keyof UserRequestsFiltersState;

        if (!filterValue) return acc;

        switch (correctlyTypedFilterName) {
          case 'channel': {
            const selectedOption = channelOptions.find(({ id }) => String(id) === String(filterValue));
            return selectedOption ? { ...acc, channel: filterValue as UserRequestsFiltersState['channel'] } : acc;
          }
          case 'service_code': {
            const selectedOption = serviceOptions.find(({ id }) => String(id) === String(filterValue));
            return selectedOption
              ? { ...acc, service_code: filterValue as UserRequestsFiltersState['service_code'] }
              : acc;
          }
          case 'service_status_name': {
            const selectedOption = statusOptions.find(({ id }) => String(id) === String(filterValue));
            return selectedOption ? { ...acc, service_status_name: String(filterValue) } : acc;
          }

          case 'created_at_from': {
            const isBeforeMaxDate = dayjs(filterValue).isBefore(createdAtStartConfig.maxDate);
            const isAfterMinDate = dayjs(filterValue).isAfter(createdAtStartConfig.minDate);
            const isDateInValidRange = isBeforeMaxDate && isAfterMinDate;

            return isFormattedDate(filterValue, '-') && isDateInValidRange
              ? {
                  ...acc,
                  [correctlyTypedFilterName]: filterValue,
                }
              : acc;
          }

          case 'created_at_to': {
            const isBeforeMaxDate = dayjs(filterValue).isBefore(createdAtEndConfig.maxDate);
            const isAfterMinDate = dayjs(filterValue).isAfter(createdAtEndConfig.minDate);
            const isDateInValidRange = isBeforeMaxDate && isAfterMinDate;

            return isFormattedDate(filterValue, '-') && isDateInValidRange
              ? {
                  ...acc,
                  [correctlyTypedFilterName]: filterValue,
                }
              : acc;
          }

          default:
            return acc;
        }
      },
      {},
    );

    setFilterValues(defaultFilterValues);
  }, [filterOptions]);

  useEffect(() => {
    closeModal();
  }, [matchLG]);

  const syncFilterValues = useCallback(
    (nextFilterValues: UserRequestsFiltersState) => {
      setFilterValues(nextFilterValues);
      const nextSearchParams = new URLSearchParams(searchParams);
      FILTER_QUERY_KEYS.forEach((key) => nextSearchParams.delete(key));
      Object.entries(nextFilterValues).forEach(([key, value]) => {
        if (!value) return;
        nextSearchParams.set(key, String(value));
      });
      nextSearchParams.set(PAGE_QUERY_KEY, String(DEFAULT_PAGE));
      setSearchParams(nextSearchParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const resetFilterValues = useCallback(() => syncFilterValues({}), [syncFilterValues]);
  const deleteFilterValue = useCallback(
    (filterNameToDelete: keyof UserRequestsFiltersState) => {
      syncFilterValues(
        Object.entries(filterValues).reduce((acc, [filterName, filterValue]) => {
          if (filterName === filterNameToDelete) return acc;

          return {
            ...acc,
            [filterName]: filterValue,
          };
        }, {}),
      );
    },
    [filterValues, syncFilterValues],
  );

  return (
    <ConditionView
      if={!matchLG}
      else={
        <>
          <FiltersModal
            isModalOpen={isOpen}
            closeModal={closeModal}
            filterProps={{
              tab,
              channelOptions,
              serviceOptions,
              statusOptions,
              createdAtStartConfig,
              createdAtEndConfig,
              filterValues,
              setFilterValues: syncFilterValues,
              isLoadingOptions,
            }}
          />
          <Button variant={ButtonVariant.SECONDARY} onClick={openModal} data-testid="filter-list-button">
            <Icon name="filter_list" />
            Фільтрація
          </Button>
        </>
      }
    >
      <Filters
        tab={tab}
        channelOptions={channelOptions}
        serviceOptions={serviceOptions}
        statusOptions={statusOptions}
        createdAtStartConfig={createdAtStartConfig}
        createdAtEndConfig={createdAtEndConfig}
        filterValues={filterValues}
        setFilterValues={syncFilterValues}
        deleteFilterValue={deleteFilterValue}
        isLoadingOptions={isLoadingOptions}
      />

      <ConditionView if={!isEmpty(filterValues)}>
        <Button variant={ButtonVariant.SECONDARY} onClick={resetFilterValues}>
          <Icon name="clear_all" />
          Очистити
        </Button>
      </ConditionView>
    </ConditionView>
  );
};

interface FiltersProps extends Pick<Props, 'tab'> {
  channelOptions: Array<SelectOption<NonNullable<UserRequestsFiltersState['channel']>>>;
  serviceOptions: Array<SelectOption<NonNullable<UserRequestsFiltersState['service_code']>>>;
  statusOptions: Array<SelectOption<NonNullable<UserRequestsFiltersState['service_status_name']>>>;
  createdAtStartConfig: Pick<DatePickerProps, 'minDate' | 'maxDate'>;
  createdAtEndConfig: Pick<DatePickerProps, 'minDate' | 'maxDate'>;
  filterValues: UserRequestsFiltersState;
  setFilterValues: (filterValues: UserRequestsFiltersState) => void;
  deleteFilterValue: (filterNameToDelete: keyof UserRequestsFiltersState) => void;
  isLoadingOptions: boolean;
}

const Filters: React.FC<FiltersProps> = ({
  tab,
  channelOptions,
  serviceOptions,
  statusOptions,
  createdAtStartConfig,
  createdAtEndConfig,
  filterValues,
  setFilterValues,
  deleteFilterValue,
  isLoadingOptions,
}) => {
  return (
    <>
      <ConditionView if={tab === 'active'}>
        <Select
          label="Де замовлено"
          allowClear
          options={channelOptions}
          disabled={isLoadingOptions}
          value={filterValues.channel}
          onChange={(value): void =>
            value ? setFilterValues({ ...filterValues, channel: value }) : deleteFilterValue('channel')
          }
          data-testid="filter-select-channel"
        />
        <Select
          label="Послуга"
          allowClear
          options={serviceOptions}
          disabled={isLoadingOptions}
          value={filterValues.service_code}
          onChange={(value): void =>
            value ? setFilterValues({ ...filterValues, service_code: value }) : deleteFilterValue('service_code')
          }
          data-testid="filter-select-service"
        />
        <Select
          label="Статус"
          allowClear
          options={statusOptions}
          disabled={isLoadingOptions}
          value={filterValues.service_status_name}
          onChange={(value): void =>
            value
              ? setFilterValues({ ...filterValues, service_status_name: String(value) })
              : deleteFilterValue('service_status_name')
          }
          data-testid="filter-select-status"
        />
        <DatePicker
          usedInModal
          label="Дата створення від"
          value={filterValues.created_at_from ? dayjs(filterValues.created_at_from) : null}
          minDate={createdAtStartConfig.minDate}
          maxDate={createdAtStartConfig.maxDate}
          onChange={(_, dateString): void => {
            if (!dateString) {
              deleteFilterValue('created_at_from');
              return;
            }
            setFilterValues({ ...filterValues, created_at_from: dateString as string });
            filterValues.created_at_to &&
              dayjs(dateString).isAfter(dayjs(filterValues.created_at_to)) &&
              deleteFilterValue('created_at_to');
          }}
          data-testid="filter-select-createdAtStart"
        />
        <DatePicker
          usedInModal
          label="Дата створення до"
          value={filterValues.created_at_to ? dayjs(filterValues.created_at_to) : null}
          minDate={createdAtEndConfig.minDate}
          maxDate={createdAtEndConfig.maxDate}
          onChange={(_, dateString): void => {
            if (!dateString) {
              deleteFilterValue('created_at_to');
              return;
            }
            setFilterValues({ ...filterValues, created_at_to: dateString as string });
          }}
          data-testid="filter-select-createdAtEnd"
        />
      </ConditionView>

      <ConditionView if={tab === 'archive'}>
        <Select
          label="Послуга"
          allowClear
          options={serviceOptions}
          disabled={isLoadingOptions}
          value={filterValues.service_code}
          onChange={(value): void =>
            value ? setFilterValues({ ...filterValues, service_code: value }) : deleteFilterValue('service_code')
          }
          data-testid="filter-select-service"
        />
      </ConditionView>
    </>
  );
};

interface FiltersModalProps {
  filterProps: Omit<FiltersProps, 'deleteFilterValue'>;
  isModalOpen: boolean;
  closeModal: () => void;
}

const FiltersModal: React.FC<FiltersModalProps> = ({
  filterProps: { filterValues, setFilterValues, ...filterProps },
  isModalOpen,
  closeModal,
}) => {
  const [filterValuesInternal, setFilterValuesInternal] = useState<UserRequestsFiltersState>(filterValues);

  useEffect(() => {
    setFilterValuesInternal(filterValues);
  }, [filterValues]);

  useEffect(() => {
    !isModalOpen && !isEqual(filterValuesInternal, filterValues) && setFilterValuesInternal(filterValues);
  }, [filterValues, filterValuesInternal, isModalOpen]);

  const deleteFilterValueInternal = useCallback(
    (filterNameToDelete: keyof UserRequestsFiltersState) => {
      setFilterValuesInternal(
        Object.entries(filterValuesInternal).reduce((acc, [filterName, filterValue]) => {
          if (filterName === filterNameToDelete) return acc;

          return {
            ...acc,
            [filterName]: filterValue,
          };
        }, {}),
      );
    },
    [filterValuesInternal],
  );

  return (
    <Modal
      open={isModalOpen}
      size="md"
      centered
      title="Фільтрація"
      onCancel={closeModal}
      footer={null}
      data-testid="filter-modal"
    >
      <Flex vertical gap={24}>
        <Filters
          {...filterProps}
          filterValues={filterValuesInternal}
          setFilterValues={setFilterValuesInternal}
          deleteFilterValue={deleteFilterValueInternal}
        />

        <Divider style={{ backgroundColor: `var(--${'grey' satisfies ColorConfig})`, margin: 0 }} />

        <Flex gap={16}>
          <Button
            style={{ flexBasis: '100%' }}
            disabled={isEqual(filterValuesInternal, filterValues)}
            onClick={(): void => {
              setFilterValues(filterValuesInternal);
              closeModal();
            }}
            data-testid="filter-modal-search-button"
          >
            Шукати
          </Button>

          <Button
            variant={ButtonVariant.SECONDARY}
            style={{ flexBasis: '100%' }}
            disabled={isEmpty(filterValuesInternal)}
            onClick={(): void => setFilterValuesInternal({})}
            data-testid="filter-modal-clear-button"
          >
            <Icon name="clear_all" />
            Очистити
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
